import { Box } from '@dealstack/frontend/shared/components/Box/Box';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { ActionIcon, Flex } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import React, { BaseSyntheticEvent, DOMAttributes, memo, useCallback } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';

interface Props {
	size?: 'sm' | 'md' | 'lg';
	closeButton?: boolean;
	children: React.ReactNode;
	className?: string;
	onClose: (event: BaseSyntheticEvent) => unknown;
	onClick?: DOMAttributes<HTMLElement>['onClick'];
	reactModalProps?: Partial<ReactModal.Props>;
	headerButtons?: React.ReactNode;
	isOpen?: boolean;
}

const SCROLLBAR_VISIBLE_CLASS = 'scrollbarVisible';

const Modal = ({
	className,
	onClick,
	onClose,
	children,
	reactModalProps,
	closeButton = true,
	isOpen = true,
}: Props) => {
	/**
	 * We check if scrollbar is visible, then we change
	 * it's sizing
	 */
	const modalRef = useCallback(
		(node: HTMLDivElement) => {
			if (!node) {
				return;
			}
			const isScrollbarVisible = node.scrollHeight > node.clientHeight;
			if (isScrollbarVisible) {
				node.classList.add(SCROLLBAR_VISIBLE_CLASS);
			} else {
				node.classList.remove(SCROLLBAR_VISIBLE_CLASS);
			}
		},
		[children]
	);

	return (
		<ReactModal
			contentLabel="modal"
			isOpen={isOpen}
			onRequestClose={onClose}
			className={className}
			{...reactModalProps}
		>
			<ModalBox ref={modalRef} onClick={onClick}>
				<ModalHeader>
					<Flex align="center" justify="end">
						{closeButton ? (
							<ActionIcon
								variant="transparent"
								onClick={onClose}
								data-testid={TEST_ELEMENT_IDS.MODAL_CLOSE_BUTTON}
							>
								<IconX size="1.5rem" />
							</ActionIcon>
						) : null}
					</Flex>
				</ModalHeader>
				<ModalContent>{children}</ModalContent>
			</ModalBox>
		</ReactModal>
	);
};

export const ModalContent = styled.div``;

export const ModalHeader = styled.div``;
export const ModalBox = styled(Box)`
	${({ theme }) => theme.shadow.z1}
	padding: 2em 0em 2em 2em;

	${ModalContent} {
		padding-right: 2rem;
	}
`;

const Memoized = memo(Modal);
const Styled = styled(Memoized)`
	${ModalBox} {
		overflow: auto;
		max-height: 80vh;

		${({ closeButton = true }) =>
			closeButton
				? css`
						padding-top: 0em;
						${ModalHeader} {
							padding-top: 1rem;
							padding-bottom: 1rem;
							padding-right: 1rem;
						}
					`
				: null}

		${({ size }) => {
			switch (size) {
				case 'sm':
					return `
                    width: clamp(12rem, 30vw, 30rem);
                `;
				case 'md':
					return `
                    width: clamp(12rem, 50vw, 60rem);
                `;
				case 'lg':
					return `
                    width: clamp(12rem, 80vw, 100rem);
                `;

				default:
					return ``;
			}
		}}
	}
`;

export { Styled as Modal };
